import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PalletPicker } from '@/components/features/worker/PalletPicker'
import { PalletWorkPanel } from '@/components/features/worker/PalletWorkPanel'
import { ScanWorkPanel } from '@/components/features/worker/ScanWorkPanel'
import { TakePalletModal } from '@/components/features/worker/TakePalletModal'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { usePalletWork } from '@/hooks/usePalletWork'
import { useWorkerPalletWebSocket } from '@/hooks/useWorkerPalletWebSocket'
import { useTaskBoxes, useTaskPallets, useWorkerTask } from '@/hooks/useWorkerTask'
import { workerRoutes } from '@/lib/navigation/routes'
import { useWorkerStore } from '@/stores/worker.store'
import { useToast } from '@/hooks/useToast'
import { extractApiError } from '@/lib/api'
import type { TaskCreationMode, TaskPalletRow } from '@/types'

const UNKNOWN_MODE_LABEL: Partial<Record<TaskCreationMode, string>> = {
  km_and_ka_file: 'КМ + КА из файлов',
  ka_file_only: 'Только КА из файлов',
  km_file_only: 'Только КМ из файла',
}

export function WorkerTaskDetailPage() {
  const { taskId = '' } = useParams()
  const { toast } = useToast()
  const workerName = useWorkerStore(s => s.workerName) || 'работник'
  const { task, isLoading } = useWorkerTask(taskId)
  const { data: pallets, isLoading: palletsLoading } = useTaskPallets(task?.uuid)
  const { takePallet, releasePallet, isReleasing } = usePalletWork(task?.uuid ?? '')

  const [activePalletNum, setActivePalletNum] = useState<number | null>(null)
  const [modalPallet, setModalPallet] = useState<TaskPalletRow | null>(null)
  const [forcePicker, setForcePicker] = useState(false)

  const myInProgress = pallets?.find(
    p => p.status === 'in_progress' && p.worker_name === workerName,
  )
  const effectiveActive = forcePicker ? null : (activePalletNum ?? myInProgress?.num ?? null)

  useWorkerPalletWebSocket(task?.uuid, workerName, effectiveActive)

  useEffect(() => {
    if (effectiveActive == null || !pallets) return
    const current = pallets.find(p => p.num === effectiveActive)
    if (!current) return
    const stillMine = current.status === 'in_progress' && current.worker_name === workerName
    if (stillMine) return
    setActivePalletNum(null)
    setForcePicker(false)
  }, [effectiveActive, pallets, workerName])

  const { data: palletBoxes = [], isLoading: boxesLoading } = useTaskBoxes(
    task?.uuid,
    effectiveActive ?? undefined,
  )

  if (isLoading || !task) {
    return isLoading ? <LoadingSpinner className="mt-20" /> : (
      <div className="text-center py-20 text-gray-500">Задание не найдено</div>
    )
  }

  if (task.status !== 'in_progress') {
    return (
      <div>
        <Link to={workerRoutes.tasks.path} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" />
          К списку заданий
        </Link>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Задание {task.id}
          </h1>
          <p className="text-gray-600">
            Задание недоступно — открыты только задания в статусе «В работе»
          </p>
        </div>
      </div>
    )
  }

  const isScanMode = task.creation_mode === 'ka_file_only'
  const isSupportedMode = !task.creation_mode || task.creation_mode === 'km_and_ka_file' || isScanMode

  if (!isSupportedMode) {
    const label = UNKNOWN_MODE_LABEL[task.creation_mode as TaskCreationMode] ?? task.creation_mode
    return (
      <div>
        <Link to={workerRoutes.tasks.path} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" />
          К списку заданий
        </Link>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Задание {task.id}
          </h1>
          <p className="text-gray-600">
            Режим «{label}» — в разработке
          </p>
        </div>
      </div>
    )
  }

  const handleRelease = async () => {
    if (effectiveActive == null) return
    try {
      await releasePallet({ palletNum: effectiveActive, workerName })
      setActivePalletNum(null)
      setForcePicker(false)
    } catch (err) {
      toast.error(extractApiError(err))
    }
  }

  return (
    <div>
      <div className="mb-6">
        <Link to={workerRoutes.tasks.path} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-3">
          <ArrowLeft className="w-4 h-4 mr-1" />
          К списку заданий
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {task.name || `Задание ${task.id}`}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {task.boxes_count} коробок · {task.pallets_count} паллет
        </p>
      </div>

      {palletsLoading || (effectiveActive != null && boxesLoading) ? (
        <LoadingSpinner className="mt-12" />
      ) : effectiveActive != null ? (
        isScanMode ? (
          <ScanWorkPanel
            taskUuid={task.uuid}
            taskId={task.id}
            palletNum={effectiveActive}
            boxes={palletBoxes}
            workerName={workerName}
            onBack={() => {
              setActivePalletNum(null)
              setForcePicker(true)
            }}
            onRelease={() => void handleRelease()}
            isReleasing={isReleasing}
          />
        ) : (
          <PalletWorkPanel
            taskUuid={task.uuid}
            palletNum={effectiveActive}
            boxes={palletBoxes}
            onBack={() => {
              setActivePalletNum(null)
              setForcePicker(true)
            }}
            onRelease={() => void handleRelease()}
            isReleasing={isReleasing}
          />
        )
      ) : (
        <PalletPicker
          pallets={pallets ?? []}
          workerName={workerName}
          onTake={setModalPallet}
          onContinue={p => {
            setForcePicker(false)
            setActivePalletNum(p.num)
          }}
        />
      )}

      <TakePalletModal
        taskUuid={task.uuid}
        creationMode={task.creation_mode}
        pallet={modalPallet}
        workerName={workerName}
        onClose={() => setModalPallet(null)}
        onTaken={num => {
          setForcePicker(false)
          setActivePalletNum(num)
          setModalPallet(null)
        }}
        takePallet={takePallet}
      />
    </div>
  )
}
